import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useMemo } from "react";
import type { FilterValues } from "@/components/profitability/filters";

/**
 * Filter params are namespaced so they cannot collide with `tab` or `range`.
 *
 * Exported because a drill-in that lands the reader on a filtered tab has to
 * set the tab AND the filter in ONE navigation -- two calls in one handler both
 * resolve against the same location, and the second spreads a `prev` that does
 * not yet hold the first, silently dropping it.
 */
export const FILTER_PARAM_PREFIX = "f_";
const PREFIX = FILTER_PARAM_PREFIX;

/**
 * Reads and writes the filter selections as `?f_<key>=` search params.
 *
 * In the URL, for the same reason `?range=` and `?tab=` are: this dashboard is
 * read by people who screenshot a figure into Slack and paste the link beside
 * it. A filtered table whose filter lives in component state reproduces as an
 * UNFILTERED table for whoever opens the link -- same screen, different number,
 * nothing on either to say why. It also survives a reload, and Back undoes a
 * filter, which is what a reader expects of something they just turned on.
 *
 * Values are real arrays rather than a delimited string: person names contain
 * commas ("Smith, John"), and any delimiter chosen here is a name that silently
 * splits into two filters later. The router serialises arrays itself.
 */
export function useFilterParams(
  keys: readonly string[],
): [FilterValues, (next: FilterValues) => void] {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();

  // The router hands back a fresh `search` object on every navigation, and the
  // values returned here feed useMemo deps in the pages that filter with them.
  // Serialising just the params this hook owns gives those pages something that
  // changes when the filters change and not merely when the router re-renders.
  const signature = JSON.stringify(keys.map((key) => search[`${PREFIX}${key}`] ?? null));

  const values = useMemo(() => {
    const raws = JSON.parse(signature) as unknown[];
    const out: FilterValues = {};
    keys.forEach((key, index) => {
      const raw = raws[index];
      // A single value arrives as a bare string when the URL was hand-edited or
      // an older link is opened; both shapes have to mean the same thing.
      out[key] = Array.isArray(raw)
        ? raw.map(String)
        : typeof raw === "string" && raw !== ""
          ? [raw]
          : [];
    });
    return out;
  }, [signature, keys]);

  // Stable identity, for the reason useRangeParam's setter is: the header
  // publishes the Filters control, and a fresh closure each render loops
  // usePageHeader.
  const setValues = useCallback(
    (next: FilterValues) => {
      navigate({
        // "." keeps the route and its path params -- this hook is shared.
        to: ".",
        // replace: applying a filter is a re-read of the same screen. Back
        // should undo the filter, not walk through every checkbox ticked.
        replace: true,
        search: (prev: Record<string, unknown>) => {
          const out = { ...prev };
          for (const key of keys) {
            const selected = next[key] ?? [];
            // An empty selection leaves the URL entirely rather than sitting
            // there as `f_person=[]`, so "no filter" has one spelling.
            out[`${PREFIX}${key}`] = selected.length === 0 ? undefined : selected;
          }
          return out;
        },
      });
    },
    [navigate, keys],
  );

  return [values, setValues];
}
